import { Avatar } from "@radix-ui/themes";
import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { uploadUrl } from "@/config/publicConfig";
import ErrorsBlock from "../../../components/errors/ErrorsBlock";
import { timezones } from "../../../enums/timezones";
import { useFetch } from "../../../hooks/useFetch";
import { useAppStore } from "../../../stores/RootStore";
import { Upload, User, UserCompact } from "../../../types/types";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input/Input";
import { Label } from "../../../ui/Label/Label";
import { ScrollArea } from "../../../ui/ScrollArea/ScrollArea";
import { SelectButton } from "../../../ui/SelectButton/SelectButton";
import ChangePasswordModal from "./ChangePasswordModal";

const Account = () => {
  const currentSite = useAppStore((store) => store.currentSite);
  const currentUser = useAppStore((store) => store.currentUser?.attributes);
  const setCurrentUser = useAppStore((store) => store.setCurrentUser);

  const locales = [
    { value: "ru", label: t("russian") },
    { value: "en", label: t("english") },
  ];

  const [imageId, setImageId] = useState(currentUser?.image?.id);

  const [errors, setErrors] = useState([]);
  const { register, reset, handleSubmit, control } = useForm({
    defaultValues: {
      first_name: currentUser?.first_name,
      last_name: currentUser?.last_name ? currentUser?.last_name : "",
      middle_name: currentUser?.middle_name ? currentUser?.middle_name : "",
      locale: {
        value: currentUser?.locale,
        label: locales.find((locale) => locale.value == currentUser?.locale)?.label,
      },
      email: currentUser?.email,
    },
  });

  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/users/account`,
    true
  );

  // Загрузка изображения
  const [
    { response: imageUploadResponse, isLoading: imageUploadIsLoading, error: imageUploadError },
    doFetchImageUpload,
  ] = useFetch(`/uploads`, true, true);

  const onSubmit = (formData: any) => {
    setErrors([]);
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          middle_name: formData.middle_name,
          locale: formData.locale.value,
          email: formData.email,
        },
      },
    });
  };

  const onFileChoice = (value: string | Blob | File) => {
    const formData = new FormData();
    formData.set("attachment", value);
    doFetchImageUpload({
      method: "POST",
      data: formData,
    });
  };

  useEffect(() => {
    if (!imageUploadResponse) {
      return;
    }
    const imageUploadData = imageUploadResponse.data as unknown as Upload;
    setImageId(imageUploadData.id);
    doFetchItemSave({
      method: "PUT",
      data: {
        data: {
          image: { ...imageUploadResponse.data },
        },
      },
    });
  }, [imageUploadResponse]);

  const editUser = (itemSaveResponse: { data: unknown }) => {
    const newUserData = itemSaveResponse.data as unknown as User;
    setCurrentUser(newUserData);
  };

  useEffect(() => {
    if (!saveResponse) return;
    if (saveResponse.data.attributes.locale != currentUser?.locale) {
      editUser(saveResponse);
      window.location.reload();
    }
    else editUser(saveResponse);
  }, [saveResponse]);

  useEffect(() => {}, [currentUser?.locale]);

  useEffect(() => {
    if (!saveError) return;
    setErrors(saveError.errors);
  }, [saveError]);

  return (
    <ScrollArea className="w-full bg-background">
      <div className="flex flex-col max-w-[900px] w-full justify-start mx-auto pl-4 pr-16 relative overflow-hidden my-[4rem]">
        <div className="text-xl font-semibold">{t("profile_settings")}</div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <label>
            <Avatar
              size="7"
              src={imageId ? uploadUrl(imageId) : ""}
              fallback={currentUser?.first_name?.[0] || "?"}
            />
            {/*@ts-ignore */}
            <input type="file" onChange={(evt) => onFileChoice(evt.target.files[0])} />
          </label>
          <div className="grid grid-cols-2 w-full pt-8 h-full gap-3">
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={`${t("name")}*`} />
                </div>
              )}
            />
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={`${t("surname")}*`} />
                </div>
              )}
            />
            <Controller
              name="middle_name"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input {...field} label={`${t("middlename")}`} />
                </div>
              )}
            />
            <Controller
              name="locale"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <div className="relative space-y-2">
                  <Label>{t("interface_language")}</Label>
                  <SelectButton
                    label={"Выберите язык"}
                    searchable={false}
                    options={locales}
                    value={value ? locales.find((s) => s.value == value.value)! : null}
                    onChange={e => {
                      onChange(e)
                    }}
                  />
                </div>
              )}
            />
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <div className="relative space-y-2">
                  <Input disabled {...field} label={`${t("email")}*`} />
                </div>
              )}
            />
            <ChangePasswordModal>
              <Button type="button" variant={"secondary"} className="mt-8">
                {t("change_password")}
              </Button>
            </ChangePasswordModal>
          </div>

          <Button type="submit" className="mt-8">
            {t("save")}
          </Button>
          {errors.length > 0 ? <ErrorsBlock errors={errors} /> : ""}
        </form>
      </div>
    </ScrollArea>
  );
};

export default Account;
